const MAX_FRACTION_DIGITS = 20;

const clampFractionDigits = (value: number): number =>
  Math.min(Math.max(0, Math.trunc(value)), MAX_FRACTION_DIGITS);

const getDecimalPlaces = (value: number): number => {
  if (!Number.isFinite(value)) {
    return 0;
  }

  const fixed = value.toFixed(MAX_FRACTION_DIGITS);
  const trimmed = fixed.replace(/\.?0+$/, '');
  const [, decimalPart = ''] = trimmed.split('.');

  return decimalPart.length;
};

const resolvePrecision = (precision?: number): number =>
  typeof precision === 'number' && Number.isFinite(precision) && precision >= 0
    ? Math.floor(precision)
    : 2;

const formatNumberWithPrecision = (
  value: number,
  precision: number | undefined,
  { treatAsFraction = false }: { treatAsFraction?: boolean } = {}
): string => {
  if (!Number.isFinite(value)) {
    return '';
  }

  const resolvedPrecision = resolvePrecision(precision);
  const scaledValue = treatAsFraction ? value * 100 : value;
  const decimalPlaces = getDecimalPlaces(scaledValue);

  const minimumFractionDigits = resolvedPrecision;

  const maximumFractionDigits = treatAsFraction
    ? resolvedPrecision
    : Math.max(resolvedPrecision, decimalPlaces);

  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: clampFractionDigits(minimumFractionDigits),
    maximumFractionDigits: clampFractionDigits(maximumFractionDigits),
    useGrouping: false,
  }).format(scaledValue);
};

/**
 * Formats a numeric value as a percentage string (e.g., "25.5%").
 *
 * @param value - The numeric value to format.
 * @param precision - Number of decimal places to display (default: 2).
 * @param options - Optional configuration:
 *   - treatAsFraction: If true, treats the number as a fraction (e.g., 0.1234 => "12.34%").
 * @returns The formatted percentage string, or "—" if the value is not a valid number.
 */
export const formatPercentage = (
  value: number,
  precision?: number,
  options?: { treatAsFraction?: boolean }
): string => {
  const formatted = formatNumberWithPrecision(value, precision, options);

  return formatted ? `${formatted}%` : '—';
};

/**
 * Formats a numeric value for display in a percentage input field.
 * Removes excess trailing zeros after the decimal, respecting the given precision.
 *
 * @param value - The numeric value to format.
 * @param precision - Number of decimal places to display.
 * @returns The formatted string suitable for input display.
 */
export const formatPercentageInputValue = (
  value: number,
  precision: number
): string => {
  const formatted = formatNumberWithPrecision(value, precision, {
    treatAsFraction: false,
  });

  if (precision > 0) {
    return formatted.replace(/\.?0+$/, '');
  }

  return formatted;
};
